import { defineStore } from 'pinia'
import { ref } from 'vue'
import { i18n } from '@/i18n'
import type { AlertDialogVariant } from '@/components/primitives/AppAlertDialog/alert-dialog-types'

export interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: AlertDialogVariant
}

interface PendingConfirm {
  title: string
  description?: string
  confirmLabel: string
  cancelLabel: string
  tone: AlertDialogVariant
  resolve: (value: boolean) => void
}

export const useDialogStore = defineStore('dialog', () => {
  const open = ref(false)
  const title = ref('')
  const description = ref<string | undefined>(undefined)
  const confirmLabel = ref(i18n.global.t('common.confirm'))
  const cancelLabel = ref(i18n.global.t('common.cancel'))
  const variant = ref<AlertDialogVariant>('default')

  const queue: PendingConfirm[] = []
  let active: PendingConfirm | null = null

  function present(next: PendingConfirm) {
    active = next
    title.value = next.title
    description.value = next.description
    confirmLabel.value = next.confirmLabel
    cancelLabel.value = next.cancelLabel
    variant.value = next.tone
    open.value = true
  }

  function settle(result: boolean) {
    open.value = false
    active?.resolve(result)
    active = null
    const next = queue.shift()
    if (next) present(next)
  }

  function confirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      const request: PendingConfirm = {
        title: options.title,
        description: options.description,
        confirmLabel: options.confirmLabel ?? i18n.global.t('common.confirm'),
        cancelLabel: options.cancelLabel ?? i18n.global.t('common.cancel'),
        tone: options.tone ?? 'default',
        resolve,
      }
      if (active) queue.push(request)
      else present(request)
    })
  }

  function onConfirm(): void {
    settle(true)
  }

  function onCancel(): void {
    settle(false)
  }

  return {
    open,
    title,
    description,
    confirmLabel,
    cancelLabel,
    variant,
    confirm,
    onConfirm,
    onCancel,
  }
})
